import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Activity, LineChart, DollarSign, Loader2, TrendingUp } from "lucide-react";

const Kpi = ({ label, value, subtitle, icon: Icon, color = "#3b82f6", testId }) => (
  <div
    className="rounded-lg border border-[#1f2937] bg-[#111827] p-4 flex flex-col gap-1"
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af]">
        {label}
      </div>
      {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
    </div>
    <div className="text-2xl font-mono font-black" style={{ color }}>{value}</div>
    {subtitle && (
      <div className="text-[10px] font-mono text-[#6b7280]">{subtitle}</div>
    )}
  </div>
);

const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
const usdSmall = (v) => `$${Number(v || 0).toFixed(4)}`;

export default function AdminOverview() {
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics/overview")
      .then((r) => setOv(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !ov) {
    return (
      <div className="flex items-center gap-2 text-[#9ca3af] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading overview…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <header>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Overview</h1>
        <div className="vx-label mt-1">System-wide stats · live</div>
      </header>

      {/* KPI row 1 — Users + Accounts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total users" value={ov.users.total} icon={Users}
             subtitle={`+${ov.users.new_this_week} this week · ${ov.users.active_today} active today`}
             testId="kpi-users-total" />
        <Kpi label="Accounts" value={ov.accounts.total} icon={Activity} color="#10b981"
             subtitle={`${ov.accounts.with_ea_online} EAs online`}
             testId="kpi-accounts-total" />
        <Kpi label="Signals today" value={ov.signals.generated_today} icon={LineChart} color="#a78bfa"
             subtitle={`${ov.signals.generated_this_week} this week`}
             testId="kpi-signals-today" />
        <Kpi label="LLM cost today" value={usdSmall(ov.llm.total_cost_today_usd)} icon={DollarSign} color="#f59e0b"
             subtitle={`${ov.llm.total_calls_today} calls · $${(ov.llm.total_cost_this_month_usd).toFixed(2)} MTD`}
             testId="kpi-llm-today" />
      </div>

      {/* KPI row 2 — Trades */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Trades today" value={ov.trades.executed_today} icon={TrendingUp} color="#3b82f6"
             testId="kpi-trades-today" />
        <Kpi label="Week P&L" value={usd(ov.trades.total_pnl_this_week_usd)}
             color={ov.trades.total_pnl_this_week_usd >= 0 ? "#10b981" : "#ef4444"}
             icon={DollarSign} testId="kpi-week-pnl" />
        <Kpi label="Signals this week" value={ov.signals.generated_this_week}
             color="#a78bfa" testId="kpi-signals-week" />
        <Kpi label="Users active today" value={ov.users.active_today}
             color="#10b981" testId="kpi-users-active" />
      </div>

      {/* Top users + top symbols */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="top-users-card">
          <div className="px-4 py-3 border-b border-[#1f2937] font-semibold text-sm">
            Top users by P&L
          </div>
          <div className="p-2">
            {ov.top_users_by_pnl.length === 0 ? (
              <div className="text-xs text-[#6b7280] p-4 font-mono">No data yet</div>
            ) : (
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-[#6b7280]">
                    <th className="text-left px-3 py-1">Email</th>
                    <th className="text-right px-3 py-1">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {ov.top_users_by_pnl.map((u) => (
                    <tr key={u.user_id} className="border-t border-[#1f2937]">
                      <td className="px-3 py-1.5 text-[#e5e7eb]">{u.email}</td>
                      <td className="px-3 py-1.5 text-right font-bold"
                          style={{ color: u.pnl_usd >= 0 ? "#10b981" : "#ef4444" }}>
                        {usd(u.pnl_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="top-symbols-card">
          <div className="px-4 py-3 border-b border-[#1f2937] font-semibold text-sm">
            Top symbols by signal count
          </div>
          <div className="p-2">
            {ov.top_symbols.length === 0 ? (
              <div className="text-xs text-[#6b7280] p-4 font-mono">No data yet</div>
            ) : (
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-[#6b7280]">
                    <th className="text-left px-3 py-1">Symbol</th>
                    <th className="text-right px-3 py-1">Signals</th>
                  </tr>
                </thead>
                <tbody>
                  {ov.top_symbols.map((s) => (
                    <tr key={s.symbol} className="border-t border-[#1f2937]">
                      <td className="px-3 py-1.5 text-[#e5e7eb]">{s.symbol}</td>
                      <td className="px-3 py-1.5 text-right text-[#3b82f6] font-bold">{s.signal_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      {/* Pattern win rates */}
      <section className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="pattern-winrates-card">
        <div className="px-4 py-3 border-b border-[#1f2937] font-semibold text-sm">
          Pattern win rates (system-wide)
        </div>
        <div className="p-2">
          {ov.pattern_win_rates.length === 0 ? (
            <div className="text-xs text-[#6b7280] p-4 font-mono">No closed trades yet</div>
          ) : (
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-[#6b7280]">
                  <th className="text-left px-3 py-1">Pattern</th>
                  <th className="text-right px-3 py-1">Trades</th>
                  <th className="text-right px-3 py-1">Win rate</th>
                </tr>
              </thead>
              <tbody>
                {ov.pattern_win_rates.map((r) => (
                  <tr key={r.pattern} className="border-t border-[#1f2937]">
                    <td className="px-3 py-1.5 text-[#e5e7eb]">{r.pattern}</td>
                    <td className="px-3 py-1.5 text-right text-[#9ca3af]">{r.trades}</td>
                    <td className="px-3 py-1.5 text-right font-bold"
                        style={{ color: r.win_rate >= 0.5 ? "#10b981" : "#f59e0b" }}>
                      {(r.win_rate * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
